import { SAVE_KEY, SCHEMA_VERSION, SETTINGS_KEY } from "../game/constants";
import { DEFAULT_DEBUG_SETTINGS, enforceDebugAccess, normalizeDebugSettings, type DebugSettings } from "../game/debugSettings";
import { DEFAULT_PERFORMANCE_SETTINGS, normalizePerformanceSettings } from "../game/performanceSettings";
import { SOLO_MODES } from "../game/soloModes";
import type { PerformanceSettings, RunState, SavePayloadV1, SoloMode } from "../game/types";

export interface UserSettings {
  musicEnabled: boolean;
  sfxEnabled: boolean;
  hapticsEnabled: boolean;
  musicVolume: number;
  sfxVolume: number;
  performance: PerformanceSettings;
  debug: DebugSettings;
}

function defaultSettings(): UserSettings {
  return {
    musicEnabled: true,
    sfxEnabled: true,
    hapticsEnabled: true,
    musicVolume: 0.35,
    sfxVolume: 0.82,
    performance: { ...DEFAULT_PERFORMANCE_SETTINGS },
    debug: { ...DEFAULT_DEBUG_SETTINGS }
  };
}

function hasStorage(): boolean {
  return typeof window !== "undefined" && window.localStorage != null;
}

export interface RunSaveState {
  run: RunState;
  tutorialRun: boolean;
}

function saveKeyForMode(mode: SoloMode): string {
  return `${SAVE_KEY}.${mode}`;
}

function normalizeLevelAdvanceBaseScore(levelScore: number, value: number | null | undefined): number {
  const normalizedLevelScore = typeof levelScore === "number" && Number.isFinite(levelScore)
    ? Math.max(0, Math.floor(levelScore))
    : 0;
  const normalizedValue = typeof value === "number" && Number.isFinite(value)
    ? Math.floor(value)
    : 0;
  return Math.max(0, Math.min(normalizedLevelScore, normalizedValue));
}

function normalizeSavedRun(run: RunState, mode: SoloMode): RunState {
  return {
    ...run,
    soloMode: run.soloMode ?? mode,
    levelAdvanceBaseScore: normalizeLevelAdvanceBaseScore(run.levelScore, run.levelAdvanceBaseScore)
  };
}

function loadRunStateContextForKey(key: string, mode: SoloMode): RunSaveState | null {
  if (!hasStorage()) return null;
  const raw = window.localStorage.getItem(key);
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SavePayloadV1>;
    if (parsed.schemaVersion !== SCHEMA_VERSION) {
      console.warn("Ignoring save due to schema mismatch");
      window.localStorage.removeItem(key);
      return null;
    }
    if (parsed.run == null) return null;
    return {
      run: normalizeSavedRun(parsed.run, mode),
      tutorialRun: parsed.tutorialRun === true
    };
  } catch {
    return null;
  }
}

function migrateLegacyClassicSave(): RunSaveState | null {
  const legacy = loadRunStateContextForKey(SAVE_KEY, "classic");
  if (legacy == null) {
    return null;
  }
  saveRunState(legacy.run, { tutorialRun: legacy.tutorialRun, mode: "classic" });
  window.localStorage.removeItem(SAVE_KEY);
  return legacy;
}

export function saveRunState(run: RunState, options?: { tutorialRun?: boolean; mode?: SoloMode }): void {
  if (!hasStorage()) return;
  const mode = options?.mode ?? run.soloMode ?? "classic";
  const payload: SavePayloadV1 = {
    schemaVersion: SCHEMA_VERSION,
    timestamp: Date.now(),
    run: normalizeSavedRun(run, mode),
    tutorialRun: options?.tutorialRun === true
  };
  window.localStorage.setItem(saveKeyForMode(mode), JSON.stringify(payload));
}

export function loadRunState(mode: SoloMode = "classic"): RunState | null {
  return loadRunStateContext(mode)?.run ?? null;
}

export function loadRunStateContext(mode: SoloMode = "classic"): RunSaveState | null {
  if (!hasStorage()) return null;
  const context = loadRunStateContextForKey(saveKeyForMode(mode), mode);
  if (context != null) {
    return context;
  }
  if (mode === "classic") {
    return migrateLegacyClassicSave();
  }
  return null;
}

export function loadSoloContinueAvailability(): Record<SoloMode, boolean> {
  return SOLO_MODES.reduce((availability, mode) => {
    availability[mode] = loadRunState(mode) != null;
    return availability;
  }, {} as Record<SoloMode, boolean>);
}

export function clearRunState(mode: SoloMode = "classic"): void {
  if (!hasStorage()) return;
  window.localStorage.removeItem(saveKeyForMode(mode));
  if (mode === "classic") {
    window.localStorage.removeItem(SAVE_KEY);
  }
}

export function saveSettings(settings: UserSettings): void {
  if (!hasStorage()) return;
  window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

const TUTORIAL_KEY = "rotatepanic.tutorial.seen.v1";

export function hasTutorialBeenSeen(): boolean {
  if (!hasStorage()) return true;
  return window.localStorage.getItem(TUTORIAL_KEY) === "1";
}

export function markTutorialSeen(): void {
  if (!hasStorage()) return;
  window.localStorage.setItem(TUTORIAL_KEY, "1");
}

const ONBOARDING_KEY = "rotatepanic.onboarding.seen.v1";

export function hasOnboardingBeenSeen(): boolean {
  if (!hasStorage()) return true;
  return window.localStorage.getItem(ONBOARDING_KEY) === "1";
}

export function markOnboardingSeen(): void {
  if (!hasStorage()) return;
  window.localStorage.setItem(ONBOARDING_KEY, "1");
}

export function resetOnboardingSeen(): void {
  if (!hasStorage()) return;
  window.localStorage.removeItem(ONBOARDING_KEY);
}

export function loadSettings(): UserSettings {
  if (!hasStorage()) return defaultSettings();
  const raw = window.localStorage.getItem(SETTINGS_KEY);
  if (raw == null) return defaultSettings();
  try {
    const parsed = JSON.parse(raw) as Partial<UserSettings>;
    return {
      musicEnabled: parsed.musicEnabled ?? true,
      sfxEnabled: parsed.sfxEnabled ?? true,
      hapticsEnabled: parsed.hapticsEnabled ?? true,
      musicVolume: Math.max(0, Math.min(1, parsed.musicVolume ?? 0.35)),
      sfxVolume: Math.max(0, Math.min(1, parsed.sfxVolume ?? 0.82)),
      performance: normalizePerformanceSettings(parsed.performance),
      debug: normalizeDebugSettings(parsed.debug)
    };
  } catch {
    return defaultSettings();
  }
}

export type DebugSettingsPersistenceMode = "account" | "local";

export interface DebugSettingsLoadResult {
  settings: DebugSettings;
  mode: DebugSettingsPersistenceMode;
}

export interface DebugSettingsSaveResult {
  settings: DebugSettings;
  mode: DebugSettingsPersistenceMode;
}

interface DebugSettingsLoadContext {
  userId: string | null;
  canUseDebugSettings: boolean;
  loadFromAccount?: (userId: string) => Promise<Partial<DebugSettings> | null>;
}

interface DebugSettingsSaveContext {
  userId: string | null;
  canUseDebugSettings: boolean;
  saveToAccount?: (userId: string, settings: DebugSettings) => Promise<boolean>;
}

export async function loadDebugSettings(context: DebugSettingsLoadContext): Promise<DebugSettingsLoadResult> {
  const localSettings = loadSettings().debug;
  const userId = context.userId;
  if (userId != null && context.loadFromAccount != null) {
    try {
      const accountRaw = await context.loadFromAccount(userId);
      if (accountRaw != null) {
        const accountSettings = enforceDebugAccess(accountRaw, context.canUseDebugSettings);
        return {
          settings: accountSettings,
          mode: "account"
        };
      }
    } catch {
      // fall through to local
    }
  }

  return {
    settings: enforceDebugAccess(localSettings, context.canUseDebugSettings),
    mode: "local"
  };
}

export async function saveDebugSettings(
  nextSettings: Partial<DebugSettings> | null | undefined,
  context: DebugSettingsSaveContext
): Promise<DebugSettingsSaveResult> {
  const settings = enforceDebugAccess(nextSettings, context.canUseDebugSettings);
  const userId = context.userId;
  if (userId != null && context.saveToAccount != null) {
    try {
      const ok = await context.saveToAccount(userId, settings);
      if (ok) {
        return {
          settings,
          mode: "account"
        };
      }
    } catch {
      // fall through to local
    }
  }

  const current = loadSettings();
  saveSettings({
    ...current,
    debug: settings
  });
  return {
    settings,
    mode: "local"
  };
}
